import locationsService from '@/api/locationsService'
import { mapState, mapActions } from 'vuex'
import { MODULE_NAME, ACTIONS } from '@/store/locations/constants'
import debounce from '@/utils/debounce'
import { ADVERT_TYPES, ADVERT_TYPE_LABELS } from '@/utils/advertOptions'
import layerService from '@/api/layerService'
import { IconLayer } from '@deck.gl/layers'
import i18n from '@/i18n'

const DEBOUNCE_TIME = 500
const iconUrl = require('@/assets/icons/map/icon-marker.svg')

export default {
  name: 'AdvertEdit',
  mixins: [],
  components: {},
  props: {},
  data () {
    return {
      advert: {
        id: null,
        name: null,
        advert_id: null,
        address: null,
        the_geom: null,
        price: null,
        impact: null,
        advert_type: null,
        format: null,
        formats_available: [],
        size: null,
        sizes_available: [],
        shop_related: { id: null, name: null },
        exclusivist: { id: null, name: null },
        costs: {
          month_price: 0,
          production_cost: 0,
          design_cost: 0,
          redesign_cost: 0
        }
      },
      exclusivistsOptions: [],
      exclusivistsOptionsFiltered: [],
      formatOptions: [],
      sizeOptions: [],
      loading: false,
      markerLayerId: 'location_edit_marker',
      tempLatLngField: '',
      tempLatLngError: '',
      tempFormat: '',
      tempSize: ''
    }
  },
  computed: {
    ...mapState(MODULE_NAME, ['shops']),
    advertid () {
      return this.$route.params.advertid
    },
    goBackLink () {
      return this.advertid === 'new' ? '/map' : `/map/advert-detail/${this.advertid}`
    },
    shopOptions () {
      return this.shops.data.map(s => ({ id: Number(s.value), name: s.label }))
    },
    advertTypes () {
      return ADVERT_TYPES
    },
    advertTypeLabels () {
      return ADVERT_TYPE_LABELS
    },
    formValid () {
      return this.advert.advert_id &&
        this.advert.town &&
        this.advert.address &&
        this.advert.advert_type &&
        this.advert.emplacement &&
        this.advert.format &&
        this.advert.size &&
        this.advert.exclusivist.id
    }
  },
  watch: {
    advertid (id) {
      this.fetchAdvert(id)
    }
  },
  async mounted () {
    this._searchShops = debounce(this.searchShops, DEBOUNCE_TIME)
    if (this.advertid !== 'new') {
      this.fetchAdvert(this.advertid)
    }

    this.enableMapClick()
    this.searchExclusivists()
    this.searchFormats()
    this.searchSizes()
    await this.searchShops('')
    const initialShop = this.$route.query.shop
    if (initialShop) {
      this.advert.shop_related = this.shopOptions.find((s) => String(s.id) === String(initialShop))
    }
  },
  beforeDestroy () {
    layerService.removeLayer(this.markerLayerId)
    this.disableMapClick()
  },
  methods: {
    ...mapActions(MODULE_NAME, [ ACTIONS.FETCH_SHOPS, ACTIONS.FETCH_ADVERTS ]),
    slowShopSearch (query) {
      this._searchShops(query)
    },
    exclusivistSeach (query) {
      this.exclusivistsOptionsFiltered = this.exclusivistsOptions.filter(e => e.name.toLowerCase().includes(query.toLowerCase()))
    },
    async searchShops (query) {
      await this[ACTIONS.FETCH_SHOPS]({ reset: true, summary: 1, q: query, ordering: 'name' })
    },
    async searchExclusivists () {
      try {
        const data = await locationsService.getExclusivists()
        this.exclusivistsOptions = data.results
        this.exclusivistsOptionsFiltered = data.results
      } catch (err) {
        // eslint-disable-next-line
        console.error('[advert-edit.js] Error fetching exclusivists data', err)
      }
    },
    normalizeString (str) {
      return str.toLowerCase().trim()
    },
    createFormat () {
      if (this.tempFormat) {
        const foundFormat = this.formatOptions.find(({ name }) => this.normalizeString(name) === this.normalizeString(this.tempFormat))
        if (foundFormat) {
          this.advert.format = foundFormat.name
        } else {
          this.formatOptions.push({ name: this.tempFormat })
          this.advert.format = this.tempFormat
        }
        this.tempFormat = ''
      }
    },
    async searchFormats () {
      try {
        const data = await locationsService.getAdvertsFormats()
        this.formatOptions = data.filter(f => f.format !== null).map(({ format }) => ({ name: format }))
      } catch (err) {
        // eslint-disable-next-line
        console.error('[advert-edit.js] Error fetching formats data', err)
      }
    },
    createSize () {
      if (this.tempSize) {
        const foundSize = this.sizeOptions.find(({ name }) => this.normalizeString(name) === this.normalizeString(this.tempSize))
        if (foundSize) {
          this.advert.size = foundSize.name
        } else {
          this.sizeOptions.push({ name: this.tempSize })
          this.advert.size = this.tempSize
        }
        this.tempSize = ''
      }
    },
    async searchSizes () {
      try {
        const data = await locationsService.getAdvertsSizes()
        this.sizeOptions = data.filter(s => s.size !== null).map(({ size }) => ({ name: size }))
      } catch (err) {
        // eslint-disable-next-line
        console.error('[advert-edit.js] Error fetching advert sizes data', err)
      }
    },
    onShopSelected (opt) {
      this.advert.shop_related = opt
      this.$refs.shopSelector.close()
    },
    onExclusivistSelected (opt) {
      this.advert.exclusivist = opt
      this.$refs.exclusivistSelector.close()
    },
    onAdvertTypeSelected (opt) {
      this.advert.advert_type = opt
      this.$refs.advertTypeSelector.close()
    },
    onSizeSelected (opt) {
      this.advert.size = opt.name
      this.$refs.sizeSelector.close()
    },
    onFormatSelected (opt) {
      this.advert.format = opt.name
      this.$refs.formatSelector.close()
    },
    isShopSelected (opt) {
      return opt.id === this.advert.shop_related.id
    },
    isExclusivistSelected (opt) {
      return opt.id === this.advert.exclusivist.id
    },
    isAdvertTypeSelected (opt) {
      return opt.id === this.advert.advert_type
    },
    isSizeSelected (opt) {
      return opt.name === this.advert.size
    },
    isFormatSelected (opt) {
      return opt.name === this.advert.format
    },

    async fetchAdvert (id) {
      this.loading = true
      try {
        const data = await locationsService.getAdvert({ id })
        this.advert = {
          ...data,
          advert_id: data.advert_id,
          price: Number(data.price),
          impact: Number(data.impact),
          shop_related: data.shop_related && {
            id: data.shop_related.id,
            name: data.shop_related.label
          }
        }
        if (!this.advert.costs) {
          this.advert.costs = {
            month_price: 0,
            production_cost: 0,
            design_cost: 0,
            redesign_cost: 0
          }
        }
        this.tempLatLngField = `${this.advert.lat}, ${this.advert.lng}`
        this.drawMarker()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[advert-edit.js] Error fetching advert data', err)
      }
      this.loading = false
    },
    drawMarker () {
      layerService.addLayer({
        id: this.markerLayerId,
        layerType: IconLayer,
        data: [this.advert],
        getPosition: d => [d.lng, d.lat],
        getIcon: () => ({
          width: 32,
          height: 32,
          url: iconUrl,
          anchorY: 32
        }),
        getSize: 48
      })
    },
    enableMapClick () {
      layerService.deckInstance.setProps({
        onClick: ({ coordinate }) => {
          this.advert.lng = coordinate[0]
          this.advert.lat = coordinate[1]
          this.tempLatLngField = `${coordinate[1]}, ${coordinate[0]}`
          this.drawMarker()
        }
      })
    },
    disableMapClick () {
      layerService.deckInstance.setProps({
        onClick: null
      })
    },
    parseTempLatLng (value) {
      this.tempLatLngField = value
      const [lat, lng] = value.split(',').map(s => s.trim())
      const hasError = isNaN(lat) || isNaN(lng)
      this.tempLatLngError = hasError ? i18n.t('INVALID_COORDS_FORMAT') : null
      this.advert.lat = Number(lat)
      this.advert.lng = Number(lng)
      this.drawMarker()
    },
    async save () {
      this.loading = true
      this.advert.name = this.advert.name ? this.advert.name : `advert_${this.advert.id}`
      let payload = {
        ...this.advert,
        shop_related: this.advert.shop_related.id,
        status: 'OPEN', // TODO: find the real value of this field and add it to the form
        the_geom: { type: 'Point', coordinates: [this.advert.lng, this.advert.lat] }
      }

      if (this.advert.exclusivist) {
        payload.exclusivist = this.advert.exclusivist.id
      }

      try {
        await locationsService.saveAdvert(payload)
        // await this[ACTIONS.FETCH_ADVERTS]({ page: 1, reset: true })
        this.$emit('update')
        this.$router.push(this.goBackLink)
      } catch (err) {
        // eslint-disable-next-line
        console.error('[advert-edti.js] Error saving advert', err)
      }
      this.loading = false
    }
  }
}
